import React, { useState, useEffect } from 'react';

const ImageSlider = ({ images, projectType, cityCardImage }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if we're on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // If it's an ITC project, just show the city card
  if (projectType === 'itc') {
    return (
      <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
        <img
          src={cityCardImage}
          alt="In The City"
          className="w-4/5 h-auto object-contain"
          style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }}
        />
      </div>
    );
  }

  // For Oui Design project, just show the oui design card
  if (projectType === 'oui') {
    return (
      <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
        <img
          src={cityCardImage}
          alt="Oui Design"
          className="w-4/5 h-auto object-contain"
          style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }}
        />
      </div>
    );
  }
  
  // For regular projects with no images
  if (!images || images.length === 0) {
    return (
      <div 
        style={{ 
          width: '100%', 
          height: '200px', 
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666'
        }}
      >
        No images available
      </div>
    );
  }
  
  // For single image
  if (images.length === 1) {
    return (
      <img
        src={images[0]}
        alt="Project"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      />
    );
  }
  
  const goToPrevious = () => {
    setCurrentIndex(current =>
      current === 0 ? images.length - 1 : current - 1
    );
  };
  
  const goToNext = () => {
    setCurrentIndex(current =>
      current === images.length - 1 ? 0 : current + 1
    );
  };
  
  // Touch event handlers for swipe functionality
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      // Swipe left - go to next
      goToNext();
    }
    
    if (touchEnd - touchStart > 75) {
      // Swipe right - go to previous
      goToPrevious();
    }
  };
  
  // Image pagination indicator
  const renderPagination = () => {
    return (
      <div
        style={{
          position: 'absolute',
          bottom: '10px',
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          zIndex: 2
        }}
      >
        {images.map((_, index) => (
          <div
            key={index}
            onClick={() => setCurrentIndex(index)}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: currentIndex === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease',
              boxShadow: '0 0 3px rgba(0, 0, 0, 0.5)'
            }}
          />
        ))}
      </div>
    );
  };
  
  return (
    <div 
      className="relative w-full"
      style={{ position: 'relative', width: '100%' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Container for images */}
      <div className="slider-images">
        {images.map((url, index) => (
          <img
            key={index}
            src={url}
            alt={`Slide ${index + 1}`}
            style={{
              display: index === currentIndex ? 'block' : 'none',
              width: '100%',
              height: 'auto',
              maxHeight: isMobile ? '300px' : '400px',
              objectFit: 'cover'
            }}
          />
        ))}
      </div>
      
      {/* Navigation buttons - only show on desktop */}
      {!isMobile && (
        <>
          <button
            onClick={goToPrevious}
            className="slider-btn left"
            style={{
               position: 'absolute',
              top: '45%',
              left: '0px',
              width: 'auto',
              padding: '16px',
              color: 'white',
              fontWeight: 'bold',
              backgroundColor: 'transparent',
              border: 'medium',
              cursor: 'pointer',
              filter: 'drop-shadow(black 0px 0px 2px)',
              fontSize: '20px',
            }}
          >
            &#10094;
          </button>
          <button
            onClick={goToNext}
            className="slider-btn right"
            style={{
              position: 'absolute',
              top: '45%',
              right: '0px',
              width: 'auto',
              padding: '16px',
              color: 'white',
              fontWeight: 'bold',
              backgroundColor: 'transparent',
              border: 'medium',
              cursor: 'pointer',
              filter: 'drop-shadow(black 0px 0px 2px)',
              fontSize: '20px',
            }}
          >
            &#10095;
          </button>
        </>
      )}
      
      {/* Pagination dots for all devices */}
      {renderPagination()}
      
      {/* Text info on how to navigate on mobile */}
      {isMobile && images.length > 1 && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            padding: '5px 8px',
            fontSize: '12px',
            borderRadius: '4px',
            pointerEvents: 'none'
          }}
        >
          Swipe to navigate
        </div>
      )}
      
      {/* Add CSS for slider container */}
      <style>
        {`
        .slider-images {
          display: flex;
          overflow: hidden;
          position: relative;
        }
        .slider-btn {
          transition: background-color 0.3s;
          z-index: 1;
        }
        .slider-btn:hover {
          background-color: rgba(0,0,0,0.5);
        }
        
        @media (max-width: 768px) {
          .slider-images img {
            max-height: 250px !important;
          }
        }
        `}
      </style>
    </div>
  );
};

export default ImageSlider;